'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React from 'react'

/* -----------------------------------------------------------------------------
 * ErrorFallback
 *
 * Deliberately plain DOM with inline styles: no UI kit, no theme, no store, no
 * router. This is what renders when something else already broke, so it must
 * not be able to break in turn — that is exactly how common's fallback turned
 * one error into a blank screen.
 * -------------------------------------------------------------------------- */

const styles: Record<string, React.CSSProperties> = {
  page: {
    boxSizing: 'border-box',
    minHeight: '100%',
    padding: 24,
    backgroundColor: '#fff',
    color: '#111',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: 13,
    lineHeight: 1.5,
    overflow: 'auto'
  },
  banner: {
    border: '2px solid #c00',
    borderLeftWidth: 8,
    padding: '12px 16px',
    marginBottom: 16,
    backgroundColor: '#fff5f5'
  },
  title: { margin: 0, fontSize: 15, fontWeight: 700, color: '#c00' },
  message: { margin: '6px 0 0', fontSize: 14, whiteSpace: 'pre-wrap' },
  label: {
    margin: '16px 0 4px',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: '#555'
  },
  pre: {
    margin: 0,
    padding: 12,
    border: '1px solid #ddd',
    backgroundColor: '#fafafa',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: 320,
    overflow: 'auto'
  },
  button: {
    marginTop: 20,
    padding: '8px 16px',
    border: '1px solid #111',
    backgroundColor: '#fff',
    color: '#111',
    font: 'inherit',
    cursor: 'pointer'
  }
}

export type ErrorFallbackProps = {
  error?: Error | null
  componentStack?: string | null
}

const ErrorFallback = ({ error, componentStack }: ErrorFallbackProps) => (
  <div style={styles.page}>
    <div style={styles.banner}>
      <p style={styles.title}>{error?.name || 'Error'} — this screen crashed</p>
      <p style={styles.message}>{error?.message || 'No message'}</p>
    </div>

    <p style={styles.label}>Stack</p>
    <pre style={styles.pre}>{error?.stack || 'No stack available'}</pre>

    {!!componentStack && (
      <React.Fragment>
        <p style={styles.label}>Component stack</p>
        <pre style={styles.pre}>{componentStack}</pre>
      </React.Fragment>
    )}

    <p style={styles.label}>Location</p>
    <pre style={styles.pre}>{window.location.href}</pre>

    <button
      type='button'
      style={styles.button}
      onClick={() => window.location.assign('/home')}
    >
      Go back to the dashboard
    </button>
  </div>
)

export default ErrorFallback
